import React from 'react'
import { first, get, has, isEmpty } from 'lodash'
import DataTable from '@ublique/components/DataTable'
import Accordion, { AccordionItem } from '@ublique/components/Accordion'
import { getOptionTranslation, isGrid } from 'pages/InputView/helpers/misc'
import { documentsStore } from 'services/state'
import PaginationSizeChooser from 'components/PaginationSizeChooser'
import NumberCellEditor from 'pages/InputView/helpers/model-info/NumberCellEditor'
import SortingList from './components/SortingList'
import PivotToggle from './components/PivotToggle'
import ItemPerPageSelector from './components/ItemPerPageSelector'

export const frameworkComponents = {
  'NumberEditor': NumberCellEditor
}

const getTranslationLabelBody = (labelKey, newValue) => (
  documentsStore.update((prevState) => {
    return {
      ...prevState,
      editedModelInfo: {
        ...prevState.editedModelInfo,
        translations: [...prevState.editedModelInfo.translations, { key: labelKey, value: newValue }]
      }
    }
  })
)

const getDefaultValueBody = (data, newValue) => {

  if (data.type === 'select') {
    if (isEmpty(data.options)) return
    newValue = data.options.find(({ label }) => label === newValue)?.value
  }
  documentsStore.update((prevState) => {
    return {
      ...prevState,
      editedModelInfo: {
        ...prevState.editedModelInfo,
        defaultValues: { ...prevState.editedModelInfo.defaultValues, [data.id]: newValue }
      }
    }
  })
}

const saveHandlerByColId = {
  labelText: (data, newValue) => getTranslationLabelBody(data.label, newValue),
  descriptionText: (data, newValue) => getTranslationLabelBody(data.description, newValue),
  defaultValue: getDefaultValueBody
}

const defaultColDef = {
  flex: 1,
  sortable: true,
  floatingFilter: true,
  resizable: true,
  filter: 'agTextColumnFilter'
}

const editableDefaultValueType = ['text', 'number'] //, 'select'

const onCellValueChangedHandler = ({ column, data, oldValue, newValue }) => {
  saveHandlerByColId[column.colId](data, newValue)
}

const getEditableTemplate = (isSuperUser) => (
  isSuperUser ? 
    '<div class="ag-cell-label-container" role="presentation">' +
    '  <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>' +
    '  <div ref="eLabel" class="ag-header-cell-label" role="presentation">' +
    '    <span class="ag-header-cell-text" style="color:#c3266b!important; padding-right:.3rem;">✎</span> <span ref="eText" class="ag-header-cell-text" role="columnheader"></span>' +
    '    <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>' +
    '    <span ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>' +
    '    <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>' +
    '    <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>' +
    '    <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>' +
    '  </div>' +
    '</div>'
    :
    '<div class="ag-cell-label-container" role="presentation">' +
    '  <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>' +
    '  <div ref="eLabel" class="ag-header-cell-label" role="presentation">' +
    '    <span ref="eText" class="ag-header-cell-text" role="columnheader"></span>' +
    '    <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>' +
    '    <span ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>' +
    '    <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>' +
    '    <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>' +
    '    <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>' +
    '  </div>' +
    '</div>'
)

const fieldsSectionColDef = (t, isSuperUser) => [
  {
    headerName: t('FIELD_NAME'),
    field: 'labelText',
    minWidth: 150,
    editable: isSuperUser,
    headerComponentParams: {
      template: getEditableTemplate(isSuperUser) 
    }
  },
  {
    headerName: t('LABEL'),
    field: 'label',
    minWidth: 150
  },
  {
    headerName: t('FIELD_DESCRIPTION'),
    field: 'descriptionText',
    minWidth: 150,
    editable: isSuperUser,
    headerComponentParams: {
      template: getEditableTemplate(isSuperUser) 
    }
  },
  {
    headerName: t('FIELD_KEY'),
    field: 'key',
    headerComponentParams: {
      template:
        '<div class="ag-cell-label-container" role="presentation">' +
        '  <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>' +
        '  <div ref="eLabel" class="ag-header-cell-label" role="presentation">' +
        '    <span class="ag-header-cell-text" style="color:#c3266b!important; padding-right:.3rem;">&#10033;</span> <span ref="eText" class="ag-header-cell-text" role="columnheader"></span>' +
        '    <span ref="eFilter" class="ag-header-icon ag-filter-icon"></span>' +
        '    <span ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>' +
        '    <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>' +
        '    <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>' +
        '    <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>' +
        '  </div>' +
        '</div>'
    }
  },
  {
    headerName: t('PARENT_TABLE'),
    field: 'parentTable'
  },
  {
    headerName: t('TYPE'),
    field: 'type'
  },
  {
    headerName: t('TYPE_EXT'),
    field: 'typeExtended',
    tooltipField: 'optionLabels'
  },
  {
    headerName: t('DEF_VALUE'),
    field: 'defaultValue',
    minWidth: '140',
    headerComponentParams: {
      template: getEditableTemplate(isSuperUser) 
    },
    editable: (params) => isSuperUser && (editableDefaultValueType.includes(params.data.type) || (params.data.type === 'select' && !isEmpty(params.data.options))),
    cellEditorSelector: params => {

      if (params.data.type === 'number') {
        return {
          component: 'NumberEditor',
        }
      }

      if (params.data.type === 'text') {
        return {
          component: 'agTextCellEditor',
        }
      }

      if (params.data.type === 'select') {
        return {
          component: 'agSelectCellEditor',
          params: {
            values: params.data.options.map((option) => option.label)
          }
        }
      }

      return undefined
    }
  },
  {
    headerName: t('HIDDEN'),
    field: 'hidden'
  },
  {
    headerName: t('LOCKED'),
    field: 'locked'
  }
]

export const formatSectionData = (
  { sections = [] },
  t,
  modelInfoT,
  labelMap
) => {
  const fields = flattenSections(sections)

  return fields.map((f) => {
    const {
      labelText,
      headerName,
      field,
      id,
      parentTable,
      position,
      hidden,
      value,
      locked,
      key
    } = f

    return {
      ...f,
      labelText: t(labelText || headerName || field || id),
      label: labelText || headerName,
      id: field || id,
      isHeaderField: !parentTable,
      parentTable: parentTable
        ? labelMap[parentTable] || parentTable
        : `${modelInfoT('HEADER')} ${position ? '- ' + modelInfoT('position') + ' ' + position : ''
        }`,
      hidden: hidden || false,
      locked: locked || false,
      key: key || false,
      typeExtended: getExtendedType(f, t, modelInfoT, labelMap),
      descriptionText:
        t(`DESCRIPTION_${labelText || headerName || field || id}`) || '',
      description: `DESCRIPTION_${labelText || headerName || field || id}`,
      ...getOptions(modelInfoT, f),
      defaultValue: getDefaultValue(value, modelInfoT)
    }
  })
}

const getOptions = (t, field) => {
  const optionsData = {
    optionsLabel: '',
    options: []
  }
  const renderInfo = field.cellEditorParams || field
  const type = field.type?.toLowerCase()
  if (type === 'select' || type === 'multiselect') {
    const options = renderInfo.options
    optionsData.options = options?.map((op) => ({ ...op, label: getOptionTranslation(t, op.label) }))
    if (options) optionsData.optionsLabel = options.map((o) => o.value).join(' | ')
    optionsData.options?.push({label: 'NULL', value: ''})
  }
  return optionsData
}

const getDefaultValue = (value, t) => {
  if (!value) return value

  switch (value.type) {
    case undefined:
      return isNaN(value) ? getOptionTranslation(t, value) : value
    case 'autogenerated':
    case 'sysdate':
    case 'scenarioDep':
    case 'remoteOptionsDep':
      return t(value.type)
    case 'detailDep':
      return value.detailTitle
  }

  if (Array.isArray(value.type)) return t('chained')
}

const flattenSections = (sections) => {
  const fields = []
  sections.forEach((section) => {
    if (section.layout) {
      fields.push(...section.fields)
    } else {
      section.fields.forEach((field) => {
        fields.push(
          ...field.columnDefs.map((el) => ({
            ...el,
            parentTable: field.id
          }))
        )
      })
    }
  })
  return fields
}

const getExtendedType = (field, t, modelInfoT, labelMap) => {
  const renderInfo = field.cellEditorParams || field
  switch (field.type?.toLowerCase()) {
    case 'autocompletelink':
    case 'multiselectlink': {
      const type = renderInfo.documentType
      return type && modelInfoT('Sidebar:DOCUMENTMODEL-' + type.toUpperCase())
    }
    case 'select':
    case 'multiselect': {
      const options = renderInfo.options
      if (options)
        return options
          .map((o) => getOptionTranslation(modelInfoT, o.label))
          .join(' | ')

      const remoteOptions = renderInfo.documentType
      const key = renderInfo.filterKey
      if (remoteOptions)
        return (
          modelInfoT('Sidebar:DOCUMENTMODEL-' + remoteOptions.toUpperCase()) +
          (key
            ? ' ' + modelInfoT('LINKED_TO') + ' ' + (labelMap[key] || key)
            : '')
        )

      return ''
    }
    case 'readonly':
      return field.labelKey
    case 'calculated':
      return getCalculatedVerbalization(field, t, modelInfoT, labelMap)
    case undefined:
      return field.value
  }
}

const DEFAULT_MAIN_ITEMS_PER_PAGE = 10
const DEFAULT_DETAIL_ITEMS_PER_PAGE = 5
const PIVOT_ENABLED = 'PIVOT_ENABLED'
const PIVOT_DISABLED = 'PIVOT_DISABLED'

const getCalculatedVerbalization = (field, t, modelInfoT, labelMap) => {
  const operation = field.operation || field.cellEditorParams.operation
  const operands = field.operands || field.cellEditorParams.operands
  const op = Array.isArray(operation) ? first(operation) : operation

  const values = !Array.isArray(operands)
    ? labelMap[operands] || operands
    : operands.map((o) => {
      var tmp = Array.isArray(o) ? first(o) : o
      if (tmp.includes('.')) {
        tmp = tmp.split('.')[1]
      }
      return labelMap[tmp] || tmp
    })
  return modelInfoT(op) + ' ' + values.join(', ')
}

const getItemPerPage = (modelDefinition, documentConfig) => {
  const ippConfig = {}

  if (has(modelDefinition, 'itemsPerPage'))
    ippConfig.main = modelDefinition.itemsPerPage
  else if (has(documentConfig, 'itemsPerPage'))
    ippConfig.main = documentConfig.itemsPerPage
  else ippConfig.main = DEFAULT_MAIN_ITEMS_PER_PAGE

  if (modelDefinition.sections.length !== 1) {
    if (has(documentConfig, 'detailItemsPerPage'))
      ippConfig.allDetail = documentConfig.detailItemsPerPage
    else ippConfig.allDetail = DEFAULT_DETAIL_ITEMS_PER_PAGE
  }

  let detailConf = null
  let detailWithConf = 0
  modelDefinition.sections.forEach((section) => {
    const det = get(section, ['fields', 0])
    if (isGrid(det) && det.itemsPerPage) {
      const value = det.itemsPerPage
      ippConfig[det.id] = value
      if(detailConf === null || detailConf === value){
        detailWithConf = detailWithConf +1
        detailConf = value
      } 
    }
  })
  console.log(detailConf, modelDefinition.sections.length, detailWithConf)
  //if all detail has the same item per page
  if(detailConf !== null && detailWithConf === modelDefinition.sections.length-1) ippConfig.allDetail = detailConf
  return ippConfig
}

const setEnablePivot = (val) => {
  documentsStore.update((prevState) => {
    return {
      ...prevState,
      editedModelInfo: {
        ...prevState.editedModelInfo,
        pivotMode: val
      }
    }
  })
}

const onPaginationSizeChanged = (section, val) => {
  const storeSection = section === 'main' ? 'itemPerPage' : 'detailItemsPerPage'
  documentsStore.update((prevState) => {
    return {
      ...prevState,
      editedModelInfo: {
        ...prevState.editedModelInfo,
        [storeSection]: val
      }
    }
  })
}

const onSortingChanged = (fieldId, selectType, val) => {
  console.log(fieldId, selectType, val)

  documentsStore.update((prevState) => {
    return {
      ...prevState,
      editedModelInfo: {
        ...prevState.editedModelInfo,
        sortingFields: {}
      }
    }
  })
}

export const formatSection = (
  modelDefinition,
  documentConfig,
  documentT,
  modelInfo,
  labelMap,
  enablePivot,
  isSuperuser
) => {
  const data = formatSectionData(
    modelDefinition,
    documentT,
    modelInfo,
    labelMap
  )
  const sortingColumns = data.filter((field) => field.sort)
  const itemPerPage = getItemPerPage(modelDefinition, documentConfig)
  // const initialPaginationSize = labelMap[config] || modelInfo(config)
  const enablePivotValue = enablePivot === undefined ? (has(modelDefinition, 'enablePivot') ? modelDefinition.enablePivot : false) : enablePivot
  return (
    <>
      <div className='model-info-section'>
        <span className='title'>{`${modelInfo('enablePivot')} : `}</span>
        <PivotToggle
          isSuperuser={isSuperuser}
          enabledLabel={modelInfo(PIVOT_ENABLED)}
          disabledLabel={modelInfo(PIVOT_DISABLED)}
          pivotValue={enablePivotValue}
          onChange={setEnablePivot} />
      </div>
      <Accordion>
        {(sortingColumns.length !== 0) && (
          <AccordionItem key='SORTING' title={modelInfo('SORTING')}>
            <SortingList 
              isSuperuser = {false}
              sortingColumns = {sortingColumns}
              data = {data}
              labelMap = {labelMap}
              modelInfo = {modelInfo}
              onChange= {onSortingChanged}/>
          </AccordionItem>
        )}

        {Object.keys(itemPerPage).length !== 0 && (
          <AccordionItem key='ITEM_PER_PAGE' title={modelInfo('ITEM_PER_PAGE')}>
            <div className='model-info-section-header-values'>
              <ItemPerPageSelector
                isSuperuser={isSuperuser}
                itemPerPage={itemPerPage}
                labelMap={labelMap}
                t={modelInfo}
                onPaginationSizeChanged={onPaginationSizeChanged}
                />
            </div>
          </AccordionItem>
        )}
      </Accordion >

      <div className='model-info-section-table'>
        <DataTable
          canSearch={false}
          rowData={data}
          columnDefs={fieldsSectionColDef(modelInfo, isSuperuser)}
          defaultColDef={defaultColDef}
          tooltipShowDelay={0}
          tooltipHideDelay={2000}
          pagination
          paginationPageSize={7}
          onCellValueChanged={onCellValueChangedHandler}
          frameworkComponents={frameworkComponents}
        />
      </div>
    </>
  )
}
